import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { FaCheck, FaCopy, FaPhoneAlt, FaShareAlt, FaWhatsapp } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import WorkWithUs from "../components/sections/WorkWithUs";
import DateSelectModal from "../components/booking/DateSelectModal";
import Swal from "../utils/swal";
import {
  buildRefundStatusStep,
  formatDistanceKmForDisplay,
  formatMoney,
  getCancelledRideBadgeMeta,
  isFullBookingCancelled,
  normalizeRefundRecord,
  resolveBookingLevelRefund,
  resolveRideRefundForDisplay,
} from "../utils/bookingFormatters";
import { launchBookingPayment } from "../utils/paymentLauncher";
import {
  getRescheduleDatePickerConfig,
  validateRescheduleAgainstSiblingRides,
} from "../utils/outstationReturnWindow";
import { getPickupMinLeadError } from "../components/booking/DateSelectModal";

const RIDE_AIRPORT = 1;
const RIDE_CITYTOUR = 2;
const RIDE_OUTSTATION = 3;
const FALLBACK_VEHICLE_IMAGE = "/assets/img/all-images/sedan-check.png";

const SUPPORT_CONTACT_NUMBER = "7535051511";
const SUPPORT_CONTACT_DISPLAY = `+91 ${SUPPORT_CONTACT_NUMBER}`;
const SUPPORT_CLIPBOARD_TEXT = `+91${SUPPORT_CONTACT_NUMBER}`;

const BookingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRideStatusExpanded, setIsRideStatusExpanded] = useState(false);
  const [isReturnRideStatusExpanded, setIsReturnRideStatusExpanded] = useState(false);
  const [isStopsExpanded, setIsStopsExpanded] = useState(false);
  const [isReturnStopsExpanded, setIsReturnStopsExpanded] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showBookingActions, setShowBookingActions] = useState(false);
  const [showOnwardRideActions, setShowOnwardRideActions] = useState(false);
  const [showReturnRideActions, setShowReturnRideActions] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactChannel, setContactChannel] = useState("call");
  const [reasonModalType, setReasonModalType] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [targetRide, setTargetRide] = useState(null);
  const [selectedScheduleDisplay, setSelectedScheduleDisplay] = useState("");
  const [datePickerConfig, setDatePickerConfig] = useState({});
  const datePickerInitialRef = useRef("");
  const [contactCopyDone, setContactCopyDone] = useState(false);
  const contactCopyTimerRef = useRef(null);

  const fetchBooking = useCallback(async () => {
    try {
      const token = localStorage.getItem("user_token");
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/bookings/${id}`,
        { headers },
      );
      if (response.data.status) setBooking(response.data.booking);
    } catch (error) {
      console.error("Error fetching booking detail:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBooking();
  }, [fetchBooking]);

  useEffect(() => {
    return () => {
      if (contactCopyTimerRef.current) {
        clearTimeout(contactCopyTimerRef.current);
        contactCopyTimerRef.current = null;
      }
    };
  }, []);

  const supportActionUrl = useMemo(
    () =>
      contactChannel === "whatsapp"
        ? `https://wa.me/${SUPPORT_CONTACT_NUMBER}`
        : `tel:${SUPPORT_CONTACT_NUMBER}`,
    [contactChannel],
  );

  const rides = Array.isArray(booking?.rides) ? booking.rides : [];
  const trips = Array.isArray(booking?.trips) ? booking.trips : [];
  const ridesData = Array.isArray(booking?.rides_data) ? booking.rides_data : [];
  const addOnItems = useMemo(() => {
    const raw = booking?.add_ons;
    let parsed = [];
    if (Array.isArray(raw)) {
      parsed = raw;
    } else if (typeof raw === "string") {
      try {
        parsed = JSON.parse(raw || "[]");
      } catch {
        parsed = [];
      }
    }

    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((item) => ({
        id: item?.id ?? item?.addon_id ?? null,
        name: String(item?.name || item?.title || "").trim(),
        price: Number(item?.price || 0),
      }))
      .filter((item) => item.name);
  }, [booking?.add_ons]);

  const firstTrip = rides[0] || trips[0] || ridesData[0] || null;
  const firstTripRides =
    ridesData.length > 0
      ? ridesData
      : Array.isArray(firstTrip?.rides_data)
        ? firstTrip.rides_data
        : [];
  const selectedVehicleTypeName = String(
    firstTripRides[0]?.vehicle_type_name ||
      firstTrip?.vehicle_type_name ||
      firstTrip?.preferred_vehicle?.name ||
      "",
  ).trim();
  const selectedVehicleTypeImage =
    booking?.vehicle_type_image_url ||
    firstTripRides[0]?.vehicle_type_image_url ||
    FALLBACK_VEHICLE_IMAGE;
  const ridesDataById = new Map();
  (Array.isArray(ridesData) ? ridesData : []).forEach((ride) => {
    const id = Number(ride?.id);
    if (!Number.isNaN(id) && id > 0) {
      ridesDataById.set(id, ride);
    }
  });
  const firstTripLocations = firstTrip?.locations || {};
  const suitcaseCount = Number(
    booking?.suitcase ??
    firstTripLocations?.suitcase ??
    firstTrip?.suitcase ??
    firstTrip?.metadata?.suitcase ??
    0,
  );
  const cityTourHours =
    firstTripLocations?.city_hours ??
    firstTrip?.city_hours ??
    firstTrip?.metadata?.city_hours ??
    null;
  const pickupLocation =
    firstTripLocations?.pickup_location || firstTrip?.pickup_location || "-";
  const dropLocation =
    firstTripLocations?.return_drop_location ||
    firstTripLocations?.drop_location ||
    firstTrip?.return_drop_location ||
    firstTrip?.drop_location ||
    firstTripRides[0]?.drop_location ||
    "-";
  const tripStops = Array.isArray(firstTrip?.stops) ? firstTrip.stops : [];

  if (loading) {
    return (
      <>
        <section className="Booking_detail_wrap section_pb section_pt">
          <div className="container placeholder-glow">
            <div className="row">
              <div className="col-12">
                <div className="bookingHeader_tabs">
                  <h3 className="placeholder col-3 rounded">
                    <span className="visually-hidden">
                      Loading booking details
                    </span>
                  </h3>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-9 col-md-8 col-sm-12 col-12">
                <div className="bookingDetail_leftMain">
                  <div className="bookingDetail_leftCol">
                    <div className="detailCol_1">
                      <p className="mb-0">
                        <span
                          className="placeholder col-3 rounded"
                          style={{ height: "24px" }}
                        ></span>
                      </p>
                      <div className="text-end w-100">
                        <h3
                          className="placeholder col-2 rounded mb-0"
                          style={{ height: "32px" }}
                        >
                          <span className="visually-hidden">
                            Loading amount
                          </span>
                        </h3>
                      </div>
                    </div>
                    <hr />
                    <div className="typeCol_2">
                      <h3
                        className="placeholder col-4 rounded mb-2"
                        style={{ height: "28px" }}
                      >
                        <span className="visually-hidden">Loading details</span>
                      </h3>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <p
                          className="placeholder col-6 rounded mb-0"
                          style={{ height: "20px" }}
                        ></p>
                      </div>
                    </div>
                    <hr />
                    <div className="detailCol_1">
                      <p className="mb-0">
                        <span
                          className="placeholder col-4 rounded"
                          style={{ height: "24px" }}
                        ></span>
                      </p>
                    </div>
                    <hr />
                    <div className="typeCol_2">
                      <div className="row">
                        <div className="col-md-6">
                          <span
                            className="placeholder col-8 rounded d-block mb-3"
                            style={{ height: "20px" }}
                          ></span>
                          <span
                            className="placeholder col-10 rounded d-block"
                            style={{ height: "20px" }}
                          ></span>
                        </div>
                        <div className="col-md-6 text-md-end">
                          <span
                            className="placeholder col-6 rounded d-block mb-3"
                            style={{ height: "20px" }}
                          ></span>
                          <span
                            className="placeholder col-8 rounded d-block"
                            style={{ height: "20px" }}
                          ></span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className="bookingDetail_rightCol bg-secondary bg-opacity-25 rounded"
                    style={{ height: "60px" }}
                  ></div>
                </div>
              </div>
              <div className="col-lg-3 col-md-4 col-sm-12 col-12">
                <div className="trip_summary trip_summaryDetail rideStatusSummary">
                  <div className="trip_summaryDetail_row">
                    <div className="trip_location trip_locationDetail trip_locationDetail2">
                      <div className="w-100">
                        <span className="placeholder col-6 rounded d-block mb-2"></span>
                        <span
                          className="placeholder col-12 rounded d-block"
                          style={{ height: "20px" }}
                        ></span>
                      </div>
                    </div>
                    <div className="trip_location trip_locationDetail trip_locationDetail2 border-0 mt-4">
                      <div className="w-100">
                        <span className="placeholder col-6 rounded d-block mb-2"></span>
                        <span
                          className="placeholder col-12 rounded d-block"
                          style={{ height: "20px" }}
                        ></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <WorkWithUs />
      </>
    );
  }

  if (!booking)
    return (
      <div className="text-center p-5">
        <h4>Booking not found</h4>
      </div>
    );

  const formatDateDM = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "short" });
    return `${day} ${month}`;
  };
  const formatDateWithYear = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime12h = (timeStr) => {
    if (!timeStr) return "";
    const [hhRaw, mmRaw] = String(timeStr).split(":");
    const hh = Number(hhRaw);
    const mm = mmRaw ?? "00";
    if (Number.isNaN(hh)) return timeStr;
    const ampm = hh >= 12 ? "PM" : "AM";
    const hour12 = hh % 12 === 0 ? 12 : hh % 12;
    return `${hour12}:${mm} ${ampm}`;
  };
  const formatDateTimeForStatus = (dateTimeValue) => {
    if (!dateTimeValue) return "-";
    const dt = new Date(dateTimeValue);
    if (Number.isNaN(dt.getTime())) return "-";
    return `${formatDateDM(dt)} - ${dt.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })}`;
  };
  const handlePayment = async (bookingId, payType = "full") => {
    try {
      await launchBookingPayment({ bookingId, payType });
    } catch (error) {
      await Swal.fire({
        title: "Error",
        text: error?.message || "Unable to start payment.",
        icon: "error",
      });
    }
  };

  const showDiscount =
    Boolean(booking.coupon_code) && Number(booking.discount_amount) > 0;

  const isInquiryBooking =
    booking.payment_mode === "inquiry" ||
    String(booking?.booking_status || "").toLowerCase() === "inquiry";

  const finalTotalNum = Number(
    booking.payable_after_cancellation ??
    booking.final_total ??
    booking.total_customer_amount ??
    0,
  );
  const cancelledRidesTotalNum = Number(booking.cancelled_rides_total ?? 0);
  const bookingTerminalCharge = Number(booking?.terminal_charge ?? 0);
  const paidAmountNum = Number(booking.paid_amount ?? 0);
  const partialAmountNum = Number(
    booking.partial_amount ??
    booking.remaining_amount ??
    0,
  );

  const remainingDueNum = isInquiryBooking
    ? 0
    : Number(booking.remaining_due ?? booking.remaining_amount ?? 0);

  const finalTotal = formatMoney(finalTotalNum);
  const paidDisplay = formatMoney(
    isInquiryBooking ? partialAmountNum : paidAmountNum,
  );
  const partialNowDisplay = formatMoney(partialAmountNum);
  const remainingDisplay = formatMoney(remainingDueNum);
  const inquiryRemainingAfterPartialDisplay = formatMoney(
    Math.max(0, finalTotalNum - partialAmountNum),
  );

  const timelineDate = firstTrip?.date
    ? formatDateWithYear(firstTrip.date)
    : firstTrip?.ride_date
      ? formatDateWithYear(firstTrip.ride_date)
      : "";
  const timelineTime = firstTrip?.pickup_time
    ? formatTime12h(firstTrip.pickup_time)
    : firstTrip?.times?.pickup_time
      ? formatTime12h(firstTrip.times.pickup_time)
      : "";
  const timelineWhen = [timelineDate, timelineTime].filter(Boolean).join(" - ");
  const isTwoWay = Number(booking?.booking_type) === 2;
  const returnRide = firstTripRides.length > 1
    ? firstTripRides[firstTripRides.length - 1]
    : null;
  const returnPickupLoc =
    firstTripLocations?.return_pickup_location ||
    firstTrip?.return_pickup_location ||
    returnRide?.pickup_location ||
    "-";
  const returnDropLoc =
    firstTripLocations?.return_drop_location ||
    firstTrip?.return_drop_location ||
    returnRide?.drop_location ||
    "-";
  const returnTripStops = Array.isArray(firstTrip?.return_stops)
    ? firstTrip.return_stops
    : Array.isArray(returnRide?.stops)
      ? returnRide.stops
      : [];
  const returnDate =
    firstTrip?.return_date ||
    firstTrip?.times?.return_date ||
    returnRide?.ride_date ||
    null;
  const returnTime =
    firstTrip?.return_time ||
    firstTrip?.times?.return_time ||
    returnRide?.pickup_time ||
    null;
  const returnWhen = [
    returnDate ? formatDateWithYear(returnDate) : "",
    returnTime ? formatTime12h(returnTime) : "",
  ]
    .filter(Boolean)
    .join(" - ");
  const onwardRideForStatus = firstTripRides[0] || firstTrip || null;
  const returnRideForStatus =
    firstTripRides.length > 1 ? firstTripRides[firstTripRides.length - 1] : null;
  const onwardDistanceLabel = formatDistanceKmForDisplay(
    onwardRideForStatus?.distance_km,
  );
  const returnDistanceLabel = formatDistanceKmForDisplay(
    returnRideForStatus?.distance_km,
  );
  const isOnwardRideCancelled =
    String(onwardRideForStatus?.status_name || "").toLowerCase().includes("cancel") ||
    Number(onwardRideForStatus?.status) === 4;
  const isReturnRideCancelled =
    String(returnRideForStatus?.status_name || "").toLowerCase().includes("cancel") ||
    Number(returnRideForStatus?.status) === 4;
  const isOnwardRideCompleted =
    String(onwardRideForStatus?.status_name || "").toLowerCase().includes("complete") ||
    Number(onwardRideForStatus?.status) === 3;
  const isOnwardRideOngoing =
    !isOnwardRideCancelled &&
    !isOnwardRideCompleted &&
    String(onwardRideForStatus?.status_name || "").toLowerCase().includes("ongoing");
  const isReturnRideCompleted =
    String(returnRideForStatus?.status_name || "").toLowerCase().includes("complete") ||
    Number(returnRideForStatus?.status) === 3;
  const formatRequestedSchedule = (requestObj) => {
    if (!requestObj?.requested_ride_date || !requestObj?.requested_pickup_time) return "";
    const dt = new Date(
      `${requestObj.requested_ride_date}T${String(requestObj.requested_pickup_time).slice(0, 5)}:00`,
    );
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };
  const getRescheduleStatusMessage = (ride) => {
    const latestRequest =
      ride?.latest_reschedule_request || ride?.pending_reschedule_request || null;
    if (!latestRequest) return "";

    const requestedAt = formatRequestedSchedule(latestRequest);
    const status = String(latestRequest?.status || "").toLowerCase();

    if (status === "approved") {
      return requestedAt
        ? "Reschedule request Approved."
        : "Reschedule request approved.";
    }

    if (status === "rejected") {
      const reason = String(latestRequest?.rejection_reason || "").trim();
      return reason
        ? `Reschedule Rejected: ${reason}`
        : "Reschedule request rejected.";
    }

    return requestedAt ? `Reschedule Requested: ${requestedAt}` : "";
  };
  const onwardRescheduleStatusMessage = getRescheduleStatusMessage(
    onwardRideForStatus,
  );
  const returnRescheduleStatusMessage = getRescheduleStatusMessage(
    returnRideForStatus,
  );
  const onwardAssignedDriver =
    onwardRideForStatus?.assigned_driver || booking?.assigned_driver || null;
  const onwardAssignedVehicle =
    onwardRideForStatus?.assigned_vehicle || booking?.assigned_vehicle || null;
  const returnAssignedDriver = returnRideForStatus?.assigned_driver || null;
  const returnAssignedVehicle = returnRideForStatus?.assigned_vehicle || null;
  const onwardRideRefund = normalizeRefundRecord(onwardRideForStatus?.refund);
  const returnRideRefund = normalizeRefundRecord(returnRideForStatus?.refund);
  const sortStatusEvents = (events = []) => [...events].sort((a, b) => {
    const aCode = Number(a?.to_status_code ?? a?.ride_status_code ?? -1);
    const bCode = Number(b?.to_status_code ?? b?.ride_status_code ?? -1);

    if (aCode === 0 && bCode !== 0) return -1;
    if (bCode === 0 && aCode !== 0) return 1;

    const aTime = a?.occurred_at ? new Date(a.occurred_at).getTime() : 0;
    const bTime = b?.occurred_at ? new Date(b.occurred_at).getTime() : 0;
    return aTime - bTime;
  });
  const getRideEventBadge = (statusCode) => {
    const code = Number(statusCode);
    if (code === 0) return "bg-warning";
    if (code === 9) return "bg-danger";
    if (code === 1 || code === 2 || code === 3) return "bg-info";
    if (code === 4) return "bg-primary";
    if (code === 5) return "bg-secondary";
    if (code === 6) return "bg-success";
    if (code === 7) return "bg-dark";
    if (code === 8) return "bg-primary";
    return "bg-secondary";
  };
  const bookingCreatedWhen = formatDateTimeForStatus(booking?.created_at);
  const returnRideCreatedWhen = formatDateTimeForStatus(
    returnRideForStatus?.created_at || booking?.created_at,
  );
  const mapStatusSteps = (
    events,
    fallbackWhen,
    fallbackLabel = "Pending",
    createdAtWhen = "-",
  ) =>
    events.length > 0
      ? events.map((event, idx) => {
        const statusCode = Number(
          event?.to_status_code ?? event?.ride_status_code ?? -1,
        );
        const statusLabel = event?.ride_status_name || "Unknown";
        const isPending =
          statusCode === 0 || statusLabel.toLowerCase() === "pending";
        const at = event?.occurred_at ? new Date(event.occurred_at) : null;
        const when = isPending
          ? createdAtWhen || "-"
          : at && !Number.isNaN(at.getTime())
            ? `${formatDateDM(at)} - ${at.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            })}`
            : fallbackWhen || "-";
        const locationName =
          event?.location?.location_name || event?.location_name || null;
        const locationLat = event?.location?.latitude ?? event?.latitude;
        const locationLng = event?.location?.longitude ?? event?.longitude;
        const hasCoords =
          locationLat != null &&
          locationLng != null &&
          !Number.isNaN(Number(locationLat)) &&
          !Number.isNaN(Number(locationLng));
        return {
          key: `event-${event?.to_status_code ?? idx}-${idx}`,
          label: statusLabel,
          badge: getRideEventBadge(statusCode),
          when,
          // Real GPS at action time only — no pickup/drop fallback.
          location: isPending || event?.location?.source === "admin"
            ? null
            : locationName ||
              (hasCoords
                ? `${Number(locationLat).toFixed(5)}, ${Number(locationLng).toFixed(5)}`
                : null),
        };
      })
      : [
        {
          key: "fallback",
          label: fallbackLabel,
          badge: "bg-warning",
          when: fallbackWhen || "-",
          location: null,
        },
      ];
  const onwardEvents = sortStatusEvents(
    Array.isArray(onwardRideForStatus?.status_events)
      ? onwardRideForStatus.status_events
      : [],
  );
  const returnEvents = sortStatusEvents(
    Array.isArray(returnRideForStatus?.status_events)
      ? returnRideForStatus.status_events
      : [],
  );
  const bookingLevelRefund = resolveBookingLevelRefund(booking?.refunds);
  const allRidesForRefund =
    firstTripRides.length > 0
      ? firstTripRides
      : Array.isArray(booking?.rides_data)
        ? booking.rides_data
        : [];
  const isFullCancel = isFullBookingCancelled(booking, allRidesForRefund);
  const cancelledRideCount = allRidesForRefund.filter((ride) => {
    const statusName = String(ride?.status_name || "").toLowerCase();
    const statusCode = Number(ride?.status);
    return statusName.includes("cancel") || statusCode === 4;
  }).length;
  const useSplitBookingRefund =
    isFullCancel &&
    Boolean(bookingLevelRefund) &&
    !onwardRideRefund &&
    !returnRideRefund &&
    cancelledRideCount > 1;
  const getRideSplitIndex = (ride) => {
    const rideId = Number(ride?.id);
    if (!Number.isNaN(rideId) && rideId > 0) {
      const idx = allRidesForRefund.findIndex((row) => Number(row?.id) === rideId);
      if (idx >= 0) return idx;
    }
    return 0;
  };
  const buildSplitRefundConfig = (ride) =>
    useSplitBookingRefund
      ? { index: getRideSplitIndex(ride), parts: cancelledRideCount }
      : null;
  const onwardRefundResolved = resolveRideRefundForDisplay({
    rideRefund: onwardRideRefund,
    bookingLevelRefund,
    isRideCancelled: isOnwardRideCancelled,
    attachBookingLevelRefund: !useSplitBookingRefund,
    splitRefund: buildSplitRefundConfig(onwardRideForStatus),
  });
  const returnRefundResolved = resolveRideRefundForDisplay({
    rideRefund: returnRideRefund,
    bookingLevelRefund,
    isRideCancelled: isReturnRideCancelled,
    attachBookingLevelRefund: false,
    splitRefund: buildSplitRefundConfig(returnRideForStatus),
  });
  const onwardRefundForStatus = onwardRefundResolved.refund;
  const returnRefundForStatus = returnRefundResolved.refund;
  const onwardCancelledBadge = getCancelledRideBadgeMeta(onwardRefundForStatus);
  const returnCancelledBadge = getCancelledRideBadgeMeta(returnRefundForStatus);

  let rideStatusSteps = mapStatusSteps(
    onwardEvents,
    bookingCreatedWhen,
    booking.status_name || "Pending",
    bookingCreatedWhen,
  );
  let returnRideStatusSteps = mapStatusSteps(
    returnEvents,
    returnRideCreatedWhen,
    "Pending",
    returnRideCreatedWhen,
  );
  const onwardRefundStep = buildRefundStatusStep(
    onwardRefundForStatus,
    onwardRefundResolved.amountOverride,
  );
  const returnRefundStep = buildRefundStatusStep(
    returnRefundForStatus,
    returnRefundResolved.amountOverride,
  );
  if (onwardRefundStep) {
    rideStatusSteps = [...rideStatusSteps, onwardRefundStep];
  }
  if (returnRefundStep) {
    returnRideStatusSteps = [...returnRideStatusSteps, returnRefundStep];
  }
  const getVisibleStatusSteps = (steps, expanded) => {
    if (!Array.isArray(steps) || steps.length <= 2 || expanded) {
      return { visibleSteps: steps || [], hiddenCount: 0 };
    }
    return {
      visibleSteps: [steps[0], steps[steps.length - 1]],
      hiddenCount: steps.length - 2,
    };
  };

  const { visibleSteps: visibleRideStatusSteps, hiddenCount: rideHiddenCount } =
    getVisibleStatusSteps(rideStatusSteps, isRideStatusExpanded);
  const { visibleSteps: visibleReturnRideStatusSteps, hiddenCount: returnRideHiddenCount } =
    getVisibleStatusSteps(returnRideStatusSteps, isReturnRideStatusExpanded);
  const hasMoreRideStatuses = Array.isArray(rideStatusSteps) && rideStatusSteps.length > 2;
  const hasMoreReturnRideStatuses =
    Array.isArray(returnRideStatusSteps) && returnRideStatusSteps.length > 2;

  const getVisibleStops = (stops, expanded) => {
    if (!Array.isArray(stops) || stops.length <= 2 || expanded) {
      return { visibleStops: stops || [], hiddenCount: 0 };
    }
    return {
      visibleStops: [stops[0], stops[stops.length - 1]],
      hiddenCount: stops.length - 2,
    };
  };

  const { visibleStops: visibleTripStops, hiddenCount: tripStopsHiddenCount } =
    getVisibleStops(tripStops, isStopsExpanded);
  const { visibleStops: visibleReturnTripStops, hiddenCount: returnTripStopsHiddenCount } =
    getVisibleStops(returnTripStops, isReturnStopsExpanded);
  const hasMoreTripStops = Array.isArray(tripStops) && tripStops.length > 2;
  const hasMoreReturnTripStops = Array.isArray(returnTripStops) && returnTripStops.length > 2;
  const statusBadgeClass = (() => {
    const status = String(booking?.booking_status || "").toLowerCase();
    if (status === "completed") return "bg-success";
    if (status === "in_progress") return "bg-info";
    if (status === "accepted") return "bg-primary";
    if (status === "requested") return "bg-warning";
    if (status === "inquiry") return "bg-secondary";
    if (status === "confirmed") return "bg-success";
    if (status === "cancelled") return "bg-danger";
    return "bg-warning";
  })();
  const isBookingCancelled =
    String(booking?.booking_status || "").toLowerCase() === "cancelled" ||
    String(booking?.status_name || "").toLowerCase().includes("cancel");
  const hasInquiryPaymentDue =
    isInquiryBooking &&
    paidAmountNum <= 0 &&
    finalTotalNum > 0 &&
    !isBookingCancelled;
  const hasOutstanding = isInquiryBooking ? hasInquiryPaymentDue : remainingDueNum > 0;
  const isPartialPaymentMode = ["partial", "not_paid"].includes(
    String(booking.payment_mode || "").toLowerCase(),
  );
  const showInquiryPayFull = hasInquiryPaymentDue;
  const showInquiryPayPartial = hasInquiryPaymentDue && partialAmountNum > 0;
  const isNonInquiry = !isInquiryBooking;
  const isUnpaidWithDue = isNonInquiry && paidAmountNum <= 0 && hasOutstanding;
  const isPaidWithDue = isNonInquiry && paidAmountNum > 0 && hasOutstanding;
  const canOfferPartial =
    isNonInquiry &&
    hasOutstanding &&
    isPartialPaymentMode &&
    partialAmountNum > 0 &&
    paidAmountNum <= 0;
  const showPayPartial = canOfferPartial;
  const showPayFull = isUnpaidWithDue;
  const showPayDue = isPaidWithDue;

  const getRideStartTime = (ride) => {
    if (!ride?.ride_date || !ride?.pickup_time) return null;
    const timePart = String(ride.pickup_time).slice(0, 5);
    const dt = new Date(`${ride.ride_date}T${timePart}:00`);
    if (Number.isNaN(dt.getTime())) return null;
    return dt;
  };

  const canMutateRide = (ride) => {
    const status = Number(ride?.status);
    if ([2, 3, 4].includes(status)) return false;
    const rideStart = getRideStartTime(ride);
    if (!rideStart) return false;
    return rideStart.getTime() - Date.now() > 12 * 60 * 60 * 1000;
  };
  const isOutstationBooking = Number(booking?.ride_type) === RIDE_OUTSTATION;
  const canCancelIndividualRide = !isOutstationBooking;
  const canShowRideActionMenu = (ride, isCancelled, isCompleted) =>
    !isInquiryBooking &&
    Boolean(ride?.id) &&
    !isCancelled &&
    !isCompleted;
  const getRideMutationBlockedMessage = (ride, mode = "cancel") => {
    const statusName = String(ride?.status_name || "").toLowerCase();
    const statusCode = Number(ride?.status);
    const isCancelled =
      statusName.includes("cancel") || statusCode === 4;
    const isCompleted =
      statusName.includes("complete") || statusCode === 2;
    const isOngoing =
      statusName.includes("ongoing") || statusCode === 3;

    if (isCancelled) {
      return mode === "reschedule"
        ? "This ride is already cancelled, so reschedule request cannot be sent."
        : "This ride is already cancelled.";
    }
    if (isCompleted) {
      return mode === "reschedule"
        ? "This ride is already completed, so reschedule request cannot be sent."
        : "This ride is already completed.";
    }
    if (isOngoing) {
      return mode === "reschedule"
        ? "This ride is already ongoing, so reschedule request cannot be sent."
        : "This ride is already ongoing.";
    }

    return mode === "reschedule"
      ? "Reschedule request can only be sent at least 12 hours before pickup time."
      : "Ride cancellation can only be done at least 12 hours before pickup time.";
  };
  const bookingRidesForMutation =
    Array.isArray(booking?.rides) && booking.rides.length > 0
      ? booking.rides
      : firstTripRides;

  const isRideActiveForCancel = (ride) => {
    const statusName = String(ride?.status_name || "").toLowerCase();
    const statusCode = Number(ride?.status);
    return (
      !statusName.includes("cancel") &&
      statusCode !== 4 &&
      !statusName.includes("complete") &&
      statusCode !== 3
    );
  };

  const activeRidesForCancel = (Array.isArray(bookingRidesForMutation)
    ? bookingRidesForMutation
    : []
  ).filter(isRideActiveForCancel);

  const canCancelBooking =
    !["completed", "cancelled"].includes(String(booking?.booking_status || "").toLowerCase()) &&
    activeRidesForCancel.length > 0 &&
    activeRidesForCancel.every((ride) => canMutateRide(ride));
  const getBookingCancellationBlockedMessage = () => {
    const bookingStatus = String(booking?.booking_status || "").toLowerCase();
    const bookingStatusName = String(booking?.status_name || "").toLowerCase();

    if (bookingStatus === "cancelled" || bookingStatusName.includes("cancel")) {
      return "This booking is already cancelled.";
    }
    if (bookingStatus === "completed" || bookingStatusName.includes("complete")) {
      return "This booking is already completed.";
    }

    const cancellableCount = activeRidesForCancel.filter((ride) => canMutateRide(ride)).length;
    if (cancellableCount > 0 && cancellableCount < activeRidesForCancel.length) {
      return isOutstationBooking
        ? "Booking can only be cancelled when every ride is at least 12 hours before pickup."
        : "Booking can only be cancelled when every ride is at least 12 hours before pickup. Please cancel individual rides instead.";
    }

    return "Cancellation can only be done at least 12 hours before pickup time.";
  };

  const parseScheduleDisplayToApi = (value) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    const yyyy = parsed.getFullYear();
    const mm = String(parsed.getMonth() + 1).padStart(2, "0");
    const dd = String(parsed.getDate()).padStart(2, "0");
    const hh = String(parsed.getHours()).padStart(2, "0");
    const mi = String(parsed.getMinutes()).padStart(2, "0");
    return {
      requested_ride_date: `${yyyy}-${mm}-${dd}`,
      requested_pickup_time: `${hh}:${mi}`,
    };
  };

  const closeReasonModal = () => {
    setShowReasonModal(false);
    setReasonModalType("");
    setReasonText("");
  };
  const openContactModal = (channel) => {
    if (contactCopyTimerRef.current) {
      clearTimeout(contactCopyTimerRef.current);
      contactCopyTimerRef.current = null;
    }
    setContactCopyDone(false);
    setContactChannel(channel === "whatsapp" ? "whatsapp" : "call");
    setShowContactModal(true);
  };
  const closeContactModal = () => {
    if (contactCopyTimerRef.current) {
      clearTimeout(contactCopyTimerRef.current);
      contactCopyTimerRef.current = null;
    }
    setContactCopyDone(false);
    setShowContactModal(false);
  };
  const handleCopySupportNumber = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(SUPPORT_CLIPBOARD_TEXT);
      } else {
        const area = document.createElement("textarea");
        area.value = SUPPORT_CLIPBOARD_TEXT;
        document.body.appendChild(area);
        area.select();
        document.execCommand("copy");
        document.body.removeChild(area);
      }
      if (contactCopyTimerRef.current) {
        clearTimeout(contactCopyTimerRef.current);
      }
      setContactCopyDone(true);
      contactCopyTimerRef.current = setTimeout(() => {
        setContactCopyDone(false);
        contactCopyTimerRef.current = null;
      }, 3000);
    } catch {
      /* no toast/modal — keep UX quiet on failure */
    }
  };
  const handleContactNow = () => {
    if (contactChannel === "whatsapp") {
      window.open(supportActionUrl, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = supportActionUrl;
    }
    closeContactModal();
  };

  const handleShareTracking = async (ride, event) => {
    event?.stopPropagation?.();
    if (!ride?.id) return;

    try {
      const token = localStorage.getItem("user_token");
      const response = await axios.get(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/ride/${ride.id}/tracking-link`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const trackingUrl = response.data?.data?.tracking_url;
      if (!trackingUrl) {
        await Swal.fire({
          toast: true,
          position: "top-end",
          icon: "warning",
          title: "Tracking link is not available yet.",
          showConfirmButton: false,
          timer: 2500,
        });
        return;
      }

      if (navigator.share) {
        await navigator.share({
          title: "Track my Orbi ride",
          text: "Live ride tracking",
          url: trackingUrl,
        });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(trackingUrl);
        await Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Tracking link copied.",
          showConfirmButton: false,
          timer: 2500,
        });
        return;
      }

      window.prompt("Copy this tracking link:", trackingUrl);
    } catch (err) {
      const message =
        err?.response?.data?.message || "Unable to fetch tracking link.";
      await Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: message,
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const handleCancelBooking = async (reason) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem("user_token");
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/bookings/${booking.id}/cancel`,
        { reason },
        { headers },
      );
      await Swal.fire({
        title: "Success",
        text: response?.data?.message || "Booking cancelled successfully.",
        icon: "success",
      });
      await fetchBooking();
    } catch (error) {
      await Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || "Unable to cancel booking.",
        icon: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelRide = async (rideId, reason) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem("user_token");
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/bookings/rides/${rideId}/cancel`,
        { reason },
        { headers },
      );
      await Swal.fire({
        title: "Success",
        text: response?.data?.message || "Ride cancelled successfully.",
        icon: "success",
      });
      await fetchBooking();
    } catch (error) {
      const apiError = error?.response?.data;
      const reasonValidationMessage = Array.isArray(apiError?.errors?.reason)
        ? apiError.errors.reason[0]
        : null;
      await Swal.fire({
        title: "Error",
        text:
          apiError?.message ||
          apiError?.error ||
          reasonValidationMessage ||
          "Unable to cancel ride.",
        icon: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRescheduleRide = async (ride, reason, displaySchedule) => {
    const payloadDateTime = parseScheduleDisplayToApi(displaySchedule);
    if (!payloadDateTime) {
      await Swal.fire({
        title: "Error",
        text: "Invalid schedule selected. Please try again.",
        icon: "error",
      });
      return;
    }
    const siblingCheck = validateRescheduleAgainstSiblingRides(
      ride,
      booking,
      displaySchedule,
    );
    if (!siblingCheck.ok) {
      await Swal.fire({
        title: "Error",
        text: siblingCheck.message,
        icon: "error",
      });
      return;
    }
    setActionLoading(true);
    try {
      const token = localStorage.getItem("user_token");
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/bookings/rides/${ride.id}/reschedule-request`,
        {
          requested_ride_date: payloadDateTime.requested_ride_date,
          requested_pickup_time: payloadDateTime.requested_pickup_time,
          reason,
        },
        { headers },
      );
      await Swal.fire({
        title: "Success",
        text: response?.data?.message || "Reschedule request submitted.",
        icon: "success",
      });
      await fetchBooking();
    } catch (error) {
      await Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || "Unable to submit request.",
        icon: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadRideInvoice = async (ride) => {
    if (!ride?.id) return;
    try {
      const token = localStorage.getItem("user_token");
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/bookings/rides/${ride.id}/invoice`,
        { headers, responseType: "blob" },
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `invoice-ride-${ride.id}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const status = error?.response?.status;
      let message = "Unable to download invoice.";
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          message = parsed?.message || parsed?.error || message;
        } catch {
          if (status === 401) {
            message = "Please log in again and retry.";
          } else if (status === 422) {
            message = "Invoice is available only after the ride is completed.";
          } else if (status >= 500) {
            message = "Server error while generating the PDF. If this continues, try again later or contact support.";
          }
        }
      } else {
        message = error?.response?.data?.message || error?.response?.data?.error || message;
      }
      if (!error?.response && error?.message) {
        message = error.message;
      }
      await Swal.fire({
        title: "Error",
        text: message,
        icon: "error",
      });
    }
  };

  const openCancelReasonModal = (type, ride = null) => {
    if (type === "cancel-booking" && !canCancelBooking) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: getBookingCancellationBlockedMessage(),
        showConfirmButton: false,
        timer: 2500,
      });
      return;
    }
    if (type === "cancel-ride" && !canCancelIndividualRide) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Outstation bookings can only be cancelled as a whole.",
        showConfirmButton: false,
        timer: 2500,
      });
      return;
    }
    if (type === "cancel-ride" && !canMutateRide(ride)) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: getRideMutationBlockedMessage(ride, "cancel"),
        showConfirmButton: false,
        timer: 2500,
      });
      return;
    }
    setReasonModalType(type);
    setTargetRide(ride);
    setReasonText("");
    setShowReasonModal(true);
  };

  const openRescheduleDateModal = (ride) => {
    if (!canMutateRide(ride)) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: getRideMutationBlockedMessage(ride, "reschedule"),
        showConfirmButton: false,
        timer: 2500,
      });
      return;
    }
    setTargetRide(ride);
    setSelectedScheduleDisplay("");
    setDatePickerConfig(getRescheduleDatePickerConfig(ride, booking));
    datePickerInitialRef.current =
      ride?.ride_date && ride?.pickup_time ? `${ride.ride_date} ${ride.pickup_time}` : "";
    const modalEl = document.getElementById("dateSelectModal");
    if (modalEl && window.bootstrap) {
      window.bootstrap.Modal.getOrCreateInstance(modalEl).show();
    }
  };

  const handleDateConfirm = (dateStr) => {
    const leadError = getPickupMinLeadError(dateStr);
    if (leadError) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: leadError,
        showConfirmButton: false,
        timer: 2800,
      });
      return;
    }
    const siblingCheck = validateRescheduleAgainstSiblingRides(
      targetRide,
      booking,
      dateStr,
    );
    if (!siblingCheck.ok) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: siblingCheck.message,
        showConfirmButton: false,
        timer: 2800,
      });
      return;
    }
    setSelectedScheduleDisplay(dateStr);
    setReasonModalType("reschedule");
    setReasonText("");
    setShowReasonModal(true);
  };
  const oldScheduleDisplay =
    targetRide?.ride_date && targetRide?.pickup_time
      ? (() => {
        const dt = new Date(`${targetRide.ride_date}T${String(targetRide.pickup_time).slice(0, 5)}:00`);
        if (Number.isNaN(dt.getTime())) return "-";
        return dt.toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      })()
      : "-";

  const submitReasonFlow = async () => {
    if (!reasonText.trim()) {
      await Swal.fire({
        title: "Error",
        text: "Reason is required.",
        icon: "error",
      });
      return;
    }
    if (reasonModalType === "reschedule" && !selectedScheduleDisplay) {
      await Swal.fire({
        title: "Error",
        text: "Please select schedule date/time first.",
        icon: "error",
      });
      return;
    }
    if ((reasonModalType === "cancel-ride" || reasonModalType === "reschedule") && !targetRide) {
      await Swal.fire({
        title: "Error",
        text: "Ride context missing.",
        icon: "error",
      });
      return;
    }

    const confirmText =
      reasonModalType === "cancel-booking"
        ? "Confirm booking cancellation?"
        : reasonModalType === "cancel-ride"
          ? "Confirm ride cancellation?"
          : "Confirm reschedule request submission?";
    const reasonPreview = reasonText?.trim() ? `Reason: ${reasonText.trim()}` : "Reason: -";
    const schedulePreview =
      reasonModalType === "reschedule" ? `<br/>Schedule: ${selectedScheduleDisplay}` : "";

    const result = await Swal.fire({
      title: "Please confirm",
      html: `${confirmText}<br/>${reasonPreview}${schedulePreview}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, submit",
      cancelButtonText: "No",
    });

    if (!result.isConfirmed) return;

    closeReasonModal();
    if (reasonModalType === "cancel-booking") {
      await handleCancelBooking(reasonText?.trim() || null);
      return;
    }
    if (reasonModalType === "cancel-ride") {
      await handleCancelRide(targetRide.id, reasonText?.trim() || null);
      return;
    }
    await handleRescheduleRide(targetRide, reasonText?.trim() || null, selectedScheduleDisplay);
  };
  const handleBookAgain = () => {
    if (!booking?.id) {
      Swal.fire({ icon: "error", text: "Booking data not available for Book Again." });
      return;
    }
    const rideType = Number(booking?.ride_type);
    const bookingTypePath =
      rideType === RIDE_AIRPORT
        ? "airport"
        : rideType === RIDE_CITYTOUR
          ? "city"
          : rideType === RIDE_OUTSTATION
            ? "outstation"
            : "airport";
    navigate(`/booking/${bookingTypePath}/re-book/${booking?.id}`);
  };

  return (
    <>
      <section className="Booking_detail_wrap section_pb section_pt">
        <div className="container">
          <div className="row mb-3">
            <div className="col-12">
              <div
                className="p-3 p-md-4 rounded-3"
                style={{ background: "linear-gradient(90deg, #fff7e2 0%, #ffffff 100%)", border: "1px solid #f3e6c3" }}
              >
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-start gap-3">
                  <div className="d-flex flex-column flex-md-row align-items-center gap-2">
                    <div className="">
                    <h3 className="mb-1 ">Booking Detail</h3>
                    <p className="mb-0 text-muted" style={{ fontSize: 14 }}>
                      Booking ID: <strong>{booking.request_id}</strong>
                    </p>
                    </div>                    
                    <img
                      src={selectedVehicleTypeImage}
                      alt={selectedVehicleTypeName || "Vehicle"}
                      style={{
                        // width: "88px",
                        height: "70px",
                        objectFit: "contain",
                        // borderRadius: "8px",
                        // background: "#fff",
                        padding: "4px",
                        // border: "1px solid #eee",
                      }}
                    />
                   
                  </div>
                  <div className="d-flex flex-column align-items-md-center align-items-center gap-2">
                    <div className="d-flex flex-wrap gap-2 justify-content-end align-items-center">
                      <button
                        type="button"
                        className="badge bg-success text-dark border text-white fw-bold"
                        style={{ cursor: "pointer" }}
                        onClick={handleBookAgain}
                      >
                        Book Again
                      </button>
                      <span className="badge bg-warning text-white fw-bold">
                        {booking.ride_type_name || "Ride"} / {booking.booking_type_name || "Trip"}
                      </span>
                      <span className={`badge ${statusBadgeClass} text-white fw-bold`}>
                        {booking.status_name || " - "}
                      </span>
                      {!String(booking?.status_name || "").toLowerCase().includes("cancel") && !String(booking?.status_name || "").toLowerCase().includes("completed") && canCancelBooking ? (
                        <div className="position-relative">
                          <button
                            type="button"
                            className="btn btn-sm btn-light border"
                            onClick={() => setShowBookingActions((prev) => !prev)}
                            aria-label="Booking actions"
                          >
                            <span style={{ fontSize: 18, lineHeight: 1 }}>⋮</span>
                          </button>
                          {showBookingActions ? (
                            <div className="dropdown-menu show" style={{ right: 0, left: "auto" }}>
                              <button
                                type="button"
                                className="dropdown-item text-danger"
                                onClick={() => {
                                  if (actionLoading) return;
                                  setShowBookingActions(false);
                                  openCancelReasonModal("cancel-booking");
                                }}
                              >
                                Cancel Booking
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    {selectedVehicleTypeName ? (
                      <span className="badge bg-light text-dark border">
                        Vehicle: {selectedVehicleTypeName}
                      </span>
                    ) : null}
                    <span className="badge bg-light text-dark border">
                      {booking.no_of_passengers} Passengers
                    </span>
                    <span className="badge bg-light text-dark border">
                      {suitcaseCount} Bags
                    </span>
                    {Number(booking.ride_type) === RIDE_CITYTOUR ? (
                      <span className="badge bg-light text-dark border">
                        City Tour: {cityTourHours || "N/A"} Hours
                      </span>
                    ) : null}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div className="booking_chat">
                    <button
                      type="button"
                      className="com_icons me-2 d-inline-flex align-items-center justify-content-center"
                      onClick={() => openContactModal("call")}
                      aria-label="Call support"
                    >
                      <FaPhoneAlt size={20} aria-hidden style={{ color: "#302B2B" }} />
                    </button>
                    <button
                      type="button"
                      className="com_icons d-inline-flex align-items-center justify-content-center"
                      onClick={() => openContactModal("whatsapp")}
                      aria-label="WhatsApp support"
                    >
                      <FaWhatsapp size={22} aria-hidden style={{ color: "#25D366" }} />
                    </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-8 col-md-7 col-sm-12 col-12 ">
              <div
                className="trip_summary trip_summaryDetail mb-3"
                style={{ cursor: hasMoreTripStops ? "pointer" : "default" }}
                onClick={() => hasMoreTripStops && setIsStopsExpanded((prev) => !prev)}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    Ride Itinerary{" "}
                    <small className="text-muted">
                      (Ride ID: {onwardRideForStatus?.id ?? "-"})
                    </small>
                  </h5>
                  <div className="d-flex align-items-center gap-2">
                  {isOnwardRideCompleted ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-dark"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadRideInvoice(onwardRideForStatus);
                      }}
                    >
                      Download Invoice
                    </button>
                  ) : null}
                  {isOnwardRideOngoing ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={(e) => handleShareTracking(onwardRideForStatus, e)}
                    >
                      <FaShareAlt className="me-1" aria-hidden />
                      Share Live Tracking 
                    </button>
                  ) : null}
                  {canShowRideActionMenu(onwardRideForStatus, isOnwardRideCancelled, isOnwardRideCompleted) ? (
                    <div className="position-relative" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="btn btn-sm btn-light border"
                        onClick={() => setShowOnwardRideActions((prev) => !prev)}
                        aria-label="Ride actions"
                      >
                        <span style={{ fontSize: 18, lineHeight: 1 }}>⋮</span>
                      </button>
                      {showOnwardRideActions ? (
                        <div className="dropdown-menu show" style={{ right: 0, left: "auto" }}>
                          {canCancelIndividualRide ? (
                            <button
                              type="button"
                              className="dropdown-item text-danger"
                              onClick={() => {
                                if (actionLoading) return;
                                setShowOnwardRideActions(false);
                                openCancelReasonModal("cancel-ride", onwardRideForStatus);
                              }}
                            >
                              Cancel Ride
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => {
                              if (actionLoading) return;
                              setShowOnwardRideActions(false);
                              if (Boolean(onwardRideForStatus?.has_pending_reschedule_request)) {
                                Swal.fire({
                                  toast: true,
                                  position: "top-end",
                                  icon: "warning",
                                  title: "Reschedule request already pending for this ride.",
                                  showConfirmButton: false,
                                  timer: 2500,
                                });
                                return;
                              }
                              openRescheduleDateModal(onwardRideForStatus);
                            }}
                          >
                            {onwardRideForStatus?.has_pending_reschedule_request
                              ? "Reschedule Requested"
                              : "Request Reschedule"}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : isOnwardRideCancelled ? (
                    <span className={`badge ${onwardCancelledBadge.badgeClass} text-white`}>
                      {onwardCancelledBadge.label}
                    </span>
                  ) : null}
                  </div>
                </div>
                <div className="trip_summaryDetail_row">
                  <div className="trip_location trip_locationDetail trip_locationDetail2">
                    <img src="/assets/img/all-images/location_on-yellow.png" alt="" />
                    <h3>
                      <small>Pick up</small> <br />
                      {pickupLocation}
                    </h3>
                  </div>
                  {visibleTripStops.length > 0 &&
                    visibleTripStops.map((stop, index) => {
                      const actualIndex = tripStops.findIndex(s => s === stop);
                      const isFirstVisible = index === 0;
                      return (
                        <React.Fragment key={stop.id || `onward-stop-${index}`}>
                          <div
                            className="trip_location trip_locationDetail trip_locationDetail2"
                          >
                            <img src="/assets/img/all-images/location_on-yellow.png" alt="" />
                            <span>
                              <small>Stop {actualIndex + 1}</small> <br />
                              {stop.stop_location || stop.address || "-"}
                            </span>
                          </div>
                          {hasMoreTripStops && !isStopsExpanded && isFirstVisible && (
                            <div
                              className="d-flex align-items-center justify-content-start my-2 mx-5"
                              style={{ color: "#000" }}
                            >
                              <div className="badge bg-light text-muted border rounded-pill px-3 py-2">
                                {tripStopsHiddenCount} more stop{tripStopsHiddenCount > 1 ? "s" : ""} hidden
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  <div className="trip_location trip_locationDetail trip_locationDetail2">
                    <img src="/assets/img/all-images/location_on-yellow.png" alt="" />
                    <h3>
                      <small>Drop</small> <br />
                      {dropLocation}
                    </h3>
                  </div>
                </div>
                <div className="trip_location">
                  <img src="/assets/img/all-images/Calendar-yellow.png" alt="" />
                  <h3>
                    <small>Pickup time</small> <br />
                    {timelineWhen || "-"}
                    {onwardRescheduleStatusMessage ? (
                      <>
                        <br />
                        <small className="text-muted">
                          {onwardRescheduleStatusMessage}
                        </small>
                      </>
                    ) : null}
                  </h3>
                </div>
                <div className="trip_location">
                  <img src="/assets/img/all-images/distance.png" alt="" />
                  <h3>
                    <small>Approximate Distance</small> <br />
                    {onwardDistanceLabel != null
                      ? `${onwardDistanceLabel} km`
                      : "—"}
                  </h3>
                </div>
                {booking.ride_type === RIDE_CITYTOUR && cityTourHours ? (
                  <div className="trip_location">
                    <img src="/assets/img/all-images/Calendar-yellow.png" alt="" />
                    <h3>
                      <small>Package Duration</small> <br />
                      {cityTourHours} Hours
                    </h3>
                  </div>
                ) : null}
                {(onwardAssignedDriver || onwardAssignedVehicle) && (
                  <div
                    className="mt-5 p-3 rounded d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3"
                    style={{ background: "#f8f9fa", border: "1px solid #ececec" }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      {onwardAssignedDriver?.image_url ? (
                        <img
                          src={onwardAssignedDriver.image_url}
                          alt="Driver"
                          style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }}
                        />
                      ) : (
                        <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#ddd" }} />
                      )}
                      <div>
                        <small className="text-muted d-block">Assigned Driver</small>
                        <strong>{onwardAssignedDriver?.name || "-"}</strong>
                      </div>
                    </div>
                    <div className="text-md-end">
                      <small className="text-muted d-block">Assigned Vehicle</small>
                      <strong>
                        {onwardAssignedVehicle?.registration_number || onwardAssignedVehicle?.name || "-"}
                      </strong>
                      <div className="text-muted" style={{ fontSize: 13 }}>
                        {onwardAssignedVehicle?.model || "-"} {onwardAssignedVehicle?.year || "-"}/{" "}
                        {onwardAssignedVehicle?.color || "-"}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {isTwoWay ? (
                <>
                  <div
                    className="trip_summary trip_summaryDetail mb-3"
                    style={{ cursor: hasMoreReturnTripStops ? "pointer" : "default" }}
                    onClick={() => hasMoreReturnTripStops && setIsReturnStopsExpanded((prev) => !prev)}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">
                        Return Ride Itinerary{" "}
                        <small className="text-muted">
                          (Ride ID: {returnRideForStatus?.id ?? "-"})
                        </small>
                      </h5>
                      <div className="d-flex align-items-center gap-2">
                      {isReturnRideCompleted ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-dark"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadRideInvoice(returnRideForStatus);
                          }}
                        >
                          Download Invoice
                        </button>
                      ) : null}
                      {canShowRideActionMenu(returnRideForStatus, isReturnRideCancelled, isReturnRideCompleted) ? (
                        <div className="position-relative" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="btn btn-sm btn-light border"
                            onClick={() => setShowReturnRideActions((prev) => !prev)}
                            aria-label="Return ride actions"
                          >
                            <span style={{ fontSize: 18, lineHeight: 1 }}>⋮</span>
                          </button>
                          {showReturnRideActions ? (
                            <div className="dropdown-menu show" style={{ right: 0, left: "auto" }}>
                              {canCancelIndividualRide ? (
                                <button
                                  type="button"
                                  className="dropdown-item text-danger"
                                  onClick={() => {
                                    if (actionLoading) return;
                                    setShowReturnRideActions(false);
                                    openCancelReasonModal("cancel-ride", returnRideForStatus);
                                  }}
                                >
                                  Cancel Ride
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => {
                                  if (actionLoading) return;
                                  setShowReturnRideActions(false);
                                  if (Boolean(returnRideForStatus?.has_pending_reschedule_request)) {
                                    Swal.fire({
                                      toast: true,
                                      position: "top-end",
                                      icon: "warning",
                                      title: "Reschedule request already pending for this ride.",
                                      showConfirmButton: false,
                                      timer: 2500,
                                    });
                                    return;
                                  }
                                  openRescheduleDateModal(returnRideForStatus);
                                }}
                              >
                                {returnRideForStatus?.has_pending_reschedule_request
                                  ? "Reschedule Requested"
                                  : "Request Reschedule"}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ) : isReturnRideCancelled ? (
                        <span className={`badge ${returnCancelledBadge.badgeClass} text-white`}>
                          {returnCancelledBadge.label}
                        </span>
                      ) : null}
                      </div>
                    </div>
                    <div className="trip_summaryDetail_row">
                      <div className="trip_location trip_locationDetail trip_locationDetail2">
                        <img src="/assets/img/all-images/location_on-yellow.png" alt="" />
                        <h3>
                          <small>Pick up</small> <br />
                          {returnPickupLoc}
                        </h3>
                      </div>
                      {visibleReturnTripStops.length > 0 &&
                        visibleReturnTripStops.map((stop, index) => {
                          const actualIndex = returnTripStops.findIndex(s => s === stop);
                          const isFirstVisible = index === 0;
                          return (
                            <React.Fragment key={stop.id || `return-stop-${index}`}>
                              <div
                                className="trip_location trip_locationDetail trip_locationDetail2"
                              >
                                <img src="/assets/img/all-images/location_on-yellow.png" alt="" />
                                <h3>
                                  <small>Stop {actualIndex + 1}</small> <br />
                                  {stop.stop_location || stop.address || "-"}
                                </h3>
                              </div>
                              {hasMoreReturnTripStops && !isReturnStopsExpanded && isFirstVisible && (
                                <div
                                  className="d-flex align-items-center justify-content-start my-2 mx-5"
                                  style={{ color: "#000" }}
                                >
                                  <div className="badge bg-light text-muted border rounded-pill px-3 py-2">
                                    {returnTripStopsHiddenCount} more stop{returnTripStopsHiddenCount > 1 ? "s" : ""} hidden
                                  </div>
                                </div>
                              )}
                            </React.Fragment>
                          );
                        })}
                      <div className="trip_location trip_locationDetail trip_locationDetail2">
                        <img src="/assets/img/all-images/location_on-yellow.png" alt="" />
                        <h3>
                          <small>Drop</small> <br />
                          {returnDropLoc}
                        </h3>
                      </div>
                    </div>
                    <div className="trip_location">
                      <img src="/assets/img/all-images/Calendar-yellow.png" alt="" />
                      <h3>
                        <small>Pickup time</small> <br />
                        {returnWhen || "-"}
                        {returnRescheduleStatusMessage ? (
                          <>
                            <br />
                            <small className="text-muted">
                              {returnRescheduleStatusMessage}
                            </small>
                          </>
                        ) : null}
                      </h3>
                    </div>
                    {/* <div className="trip_location">
                      <img src="/assets/img/all-images/distance.png" alt="" />
                      <h3>
                        <small>Distance</small> <br />
                        {returnDistanceLabel != null
                          ? `${returnDistanceLabel} km`
                          : "—"}
                      </h3>
                    </div> */}
                    {isTwoWay && (returnAssignedDriver || returnAssignedVehicle) ? (
                      <div
                        className="mt-5 p-3 rounded d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3"
                        style={{ background: "#f8f9fa", border: "1px solid #ececec" }}
                      >
                        <div className="d-flex align-items-center gap-3">
                          {returnAssignedDriver?.image_url ? (
                            <img
                              src={returnAssignedDriver.image_url}
                              alt="Driver"
                              style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }}
                            />
                          ) : (
                            <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#ddd" }} />
                          )}
                          <div>
                            <small className="text-muted d-block">Assigned Driver</small>
                            <strong>{returnAssignedDriver?.name || "-"}</strong>
                          </div>
                        </div>
                        <div className="text-md-end">
                          <small className="text-muted d-block">Assigned Vehicle</small>
                          <strong>
                            {returnAssignedVehicle?.registration_number || returnAssignedVehicle?.name || "-"}
                          </strong>
                          <div className="text-muted" style={{ fontSize: 13 }}>
                            {returnAssignedVehicle?.model || "-"} {returnAssignedVehicle?.year || "-"}/{" "}
                            {returnAssignedVehicle?.color || "-"}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>

                </>
              ) : null}


            </div>

            <div className="col-lg-4 col-md-5 col-sm-12 col-12 order-1 order-md-1 order-lg-2">
              <div className="trip_summary trip_summaryDetail mb-3">
                <h5 className="mb-3">Payment Summary</h5>
                <div className="project_detail d-flex justify-content-between align-items-center">
                  <p className="title mb-0">Rides Total</p><br />
                  <p className="mb-0">₹{formatMoney(booking.rides_total)}</p>
                </div>
                {Number(booking.add_ons_total) > 0 ? (
                  <>
                    {/* <div className="project_detail d-flex justify-content-between align-items-center">
                      <p className="title mb-0">Add-ons Total</p>
                      <p className="mb-0 text-secondary">+₹{formatMoney(booking.add_ons_total)}</p>
                    </div> */}
                    {addOnItems.map((addon, idx) => (
                      <div
                        key={`${addon.id ?? addon.name}-${idx}`}
                        className="project_detail d-flex justify-content-between align-items-center"
                        style={{ paddingTop: "4px", paddingBottom: "4px" }}
                      >
                        <p
                          className="mb-0 text-muted"
                          title={addon.name}
                          style={{
                            maxWidth: "70%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                           {addon.name}
                        </p>
                        <p className="mb-0 text-secondary" >
                          +₹{formatMoney(addon.price)}
                        </p>
                      </div>
                    ))}
                  </>
                ) : null}
                {bookingTerminalCharge > 0 ? (
                  <div className="project_detail d-flex justify-content-between align-items-center">
                    <p className="title mb-0">Terminal Surcharge</p>
                    <p className="mb-0 text-secondary">+₹{formatMoney(bookingTerminalCharge)}</p>
                  </div>
                ) : null}
                {showDiscount ? (
                  <div className="project_detail d-flex justify-content-between align-items-center">
                    <p className="title mb-0">Coupon Discount</p>
                    <p className="mb-0 text-info">
                      -₹{formatMoney(booking.coupon_discount_amount || booking.discount_amount)}
                    </p>
                  </div>
                ) : null}
                {Number(booking.extra_discount) > 0 ? (
                  <div className="project_detail d-flex justify-content-between align-items-center">
                    <p className="title mb-0">Extra Discount</p>
                    <p className="mb-0 text-info">-₹{formatMoney(booking.extra_discount)}</p>
                  </div>
                ) : null}
                {Math.abs(Number(booking.customer_fare_adjustments_net ?? 0)) > 0 ? (
                  <div className="project_detail d-flex justify-content-between align-items-center">
                    <p className="title mb-0">Customer Fare Adjustments</p>
                    <p
                      className={
                        Number(booking.customer_fare_adjustments_net) >= 0
                          ? "mb-0 text-secondary"
                          : "mb-0 text-info"
                      }
                    >
                      {Number(booking.customer_fare_adjustments_net) >= 0 ? "+" : "-"}₹
                      {formatMoney(Math.abs(Number(booking.customer_fare_adjustments_net)))}
                    </p>
                  </div>
                ) : null}
                {cancelledRidesTotalNum > 0 ? (
                  <div className="project_detail d-flex justify-content-between align-items-center">
                    <p className="title mb-0">Cancelled Rides</p>
                    <p className="mb-0 text-info">-₹{formatMoney(cancelledRidesTotalNum)}</p>
                  </div>
                ) : null}
                <div
                  className="project_detail d-flex justify-content-between align-items-center"
                  style={{ borderTop: "1px solid #eee", paddingTop: "8px", marginTop: "8px" }}
                >
                  <p className="title mb-0">
                    <strong>{isInquiryBooking ? "Estimated Total" : "Final Total"}</strong>
                  </p>
                  <p className="mb-0">
                    <strong>₹{finalTotal}</strong>
                  </p>
                </div>
                <div className="project_detail d-flex justify-content-between align-items-center">
                  <p className="title mb-0">
                    {isInquiryBooking ? "Partial Amount" : "Paid"}
                  </p>
                  <p className={isInquiryBooking ? "mb-0 text-muted" : "mb-0 text-success"}>
                    <strong>₹{paidDisplay}</strong>
                  </p>
                </div>
               
                {!isInquiryBooking ? (
                  <div className="project_detail d-flex justify-content-between align-items-center">
                    <p className="title mb-0">Remaining Due</p>
                    <p className="mb-0 text-danger">
                      <strong>₹{remainingDisplay}</strong>
                    </p>
                  </div>
                ) : null}
                {isInquiryBooking && hasInquiryPaymentDue ? (
                  <div
                    className="mt-2 p-2 rounded"
                    style={{ background: "#fffdf6", border: "1px solid #f0ddb0" }}
                  >
                    <p className="mb-1" style={{ fontSize: 13 }}>
                      If you pay <strong>Partial</strong> now: <strong>₹{partialNowDisplay}</strong>
                    </p>
                    <p className="mb-1" style={{ fontSize: 13 }}>
                      If you pay <strong>Full</strong> now: <strong>₹{finalTotal}</strong>
                    </p>
                    <p className="mb-0 text-muted" style={{ fontSize: 12 }}>
                      Remaining after partial payment: ₹{inquiryRemainingAfterPartialDisplay}
                    </p>
                  </div>
                ) : canOfferPartial ? (
                  <div
                    className="mt-2 p-2 rounded"
                    style={{ background: "#fffdf6", border: "1px solid #f0ddb0" }}
                  >
                    <p className="mb-1" style={{ fontSize: 13 }}>
                      If you pay <strong>Partial</strong> now: <strong>₹{partialNowDisplay}</strong>
                    </p>
                    <p className="mb-0 text-muted" style={{ fontSize: 12 }}>
                      Remaining after partial payment: ₹{remainingDisplay}
                    </p>
                  </div>
                ) : null}
                {hasOutstanding && !isBookingCancelled ? (
                <div className="mt-3 d-flex flex-wrap gap-md-2 gap-1 justify-content-between">
                  {showInquiryPayPartial ? (
                    <button
                      type="button"
                      onClick={() => handlePayment(booking.id, "partial")}
                      className="btn btn-warning fw-bold px-md-4 px-2 border-0 rounded-pill"
                      style={{ fontSize: "15px" }}
                    >
                      <i className="fa fa-money me-2" />
                      Pay Partial Amount
                    </button>
                  ) : null}
                  {showInquiryPayFull ? (
                    <button
                      type="button"
                      onClick={() => handlePayment(booking.id, "full")}
                      className="btn btn-warning fw-bold px-md-4 px-2 border-0 rounded-pill"
                      style={{ fontSize: "15px" }}
                    >
                      <i className="fa fa-credit-card me-2" />
                      Pay Full Amount
                    </button>
                  ) : null}
                  {showPayPartial ? (
                    <button
                      type="button"
                      onClick={() => handlePayment(booking.id, "partial")}
                      className="btn btn-warning fw-bold px-4 border-0 rounded-pill"
                    >
                      <i className="fa fa-money me-1" />
                      Pay Partial Amount
                    </button>
                  ) : null}
                  {showPayFull ? (
                    <button
                      type="button"
                      onClick={() => handlePayment(booking.id, "full")}
                      className="btn btn-warning fw-bold px-4 border-0 rounded-pill"
                    >
                      <i className="fa fa-credit-card me-2" />
                      Pay Full Amount
                    </button>
                  ) : null}
                  {showPayDue ? (
                    <button
                      type="button"
                      onClick={() => handlePayment(booking.id, "remaining")}
                      className="btn btn-warning fw-bold px-4 border-0 rounded-pill"
                    >
                      <i className="fa fa-money me-2" />
                      Pay Due Amount
                    </button>
                  ) : null}
                </div>
                ) : null}
              </div>

              <div
                className="trip_summary trip_summaryDetail rideStatusSummary"
                style={{ overflow: "hidden" }}
                onClick={() => setIsRideStatusExpanded((prev) => !prev)}
              >
                <div className="ridestatus_heading">
                  <h3>Ride Status</h3>
                  {hasMoreRideStatuses ? (
                    <small className="text-muted d-block" style={{ fontSize: 11 }}>
                      {isRideStatusExpanded ? "Tap to collapse" : "Tap to view all statuses"}
                      {" "}
                      {isRideStatusExpanded ? "\u25B2" : "\u25BC"}
                    </small>
                  ) : null}
                </div>
                <div className="booking_location booking_rideStatus">
                  {visibleRideStatusSteps.map((step, index) => (
                    <React.Fragment key={step.key}>
                      <div className="location_col location_col2">
                        <img alt="" src="/assets/img/all-images/location_on.png" />
                        <p className="mb-0 ride_status_detail">
                          <span
                            className={`me-2 badge ${step.badge} text-white status_detail status_detail`}
                          >
                            {step.label}
                          </span>
                          <span>{step.when}</span>
                          <br />
                          {step.location ? (
                            <>
                              <small className="text-muted">{step.location}</small>
                              <br />
                            </>
                          ) : null}
                        </p>
                      </div>

                      {!isRideStatusExpanded && rideHiddenCount > 0 && index === 0 && (
                        <div className="location_col location_col2" style={{ minHeight: "50px", opacity: 0.7 }}>
                          <img alt="" src="/assets/img/all-images/location_on.png" style={{ visibility: "hidden" }} />
                          <div
                            className="d-flex align-items-center justify-content-center bg-light rounded-pill border"
                            style={{
                              padding: "2px 10px",
                              fontSize: "11px",
                              color: "#666",
                              marginLeft: "10px",
                              marginTop: "-15px",
                              width: "fit-content",
                              cursor: "pointer",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                            }}
                          >
                            <i className="fa fa-ellipsis-v me-2"></i>
                            {rideHiddenCount} more status{rideHiddenCount > 1 ? "es" : ""} hidden
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              {isTwoWay ? (
                <div
                  className="trip_summary trip_summaryDetail rideStatusSummary mt-3 mb-3"
                  style={{ overflow: "hidden" }}
                  onClick={() =>
                    setIsReturnRideStatusExpanded((prev) => !prev)
                  }
                >
                  <div className="ridestatus_heading">
                    <h3>Return Ride Status</h3>
                    {hasMoreReturnRideStatuses ? (
                      <small className="text-muted d-block" style={{ fontSize: 11 }}>
                        {isReturnRideStatusExpanded ? "Tap to collapse" : "Tap to view all statuses"}
                        {" "}
                        {isReturnRideStatusExpanded ? "\u25B2" : "\u25BC"}
                      </small>
                    ) : null}
                  </div>
                  <div className="booking_location booking_rideStatus">
                    {visibleReturnRideStatusSteps.map((step, index) => (
                      <React.Fragment key={`return-${step.key}`}>
                        <div className="location_col location_col2">
                          <img alt="" src="/assets/img/all-images/location_on.png" />
                          <p className="mb-0 ride_status_detail">
                            <span
                              className={`me-2 badge ${step.badge} text-white status_detail status_detail`}
                            >
                              {step.label}
                            </span>
                            <span>{step.when}</span>
                            <br />
                            {step.location ? (
                              <>
                                <small className="text-muted">{step.location}</small>
                                <br />
                              </>
                            ) : null}
                          </p>
                        </div>

                        {!isReturnRideStatusExpanded && returnRideHiddenCount > 0 && index === 0 && (
                          <div className="location_col location_col2" style={{ minHeight: "50px", opacity: 0.7 }}>
                            <img alt="" src="/assets/img/all-images/location_on.png" style={{ visibility: "hidden" }} />
                            <div
                              className="d-flex align-items-center justify-content-center bg-light rounded-pill border"
                              style={{
                                padding: "2px 10px",
                                fontSize: "11px",
                                color: "#666",
                                width: "fit-content",
                                cursor: "pointer",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                              }}
                            >
                              <i className="fa fa-ellipsis-v me-2"></i>
                              {returnRideHiddenCount} more status{returnRideHiddenCount > 1 ? "es" : ""} hidden
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>
      {showReasonModal ? (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.45)" }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {reasonModalType === "reschedule" ? "Reschedule Reason" : "Cancellation Reason"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeReasonModal}
                />
              </div>
              <div className="modal-body">
                {reasonModalType === "reschedule" ? (
                  <>
                    <p className="mb-2">
                      <strong>Old Date:</strong> {oldScheduleDisplay}
                    </p>
                    <p className="mb-2">
                      <strong>Selected Schedule:</strong> {selectedScheduleDisplay || "-"}
                    </p>
                  </>
                ) : null}
                <label className="form-label">Reason</label>
                <textarea
                  className="form-control"
                  required
                  rows={4}
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  placeholder="Write reason here..."
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeReasonModal}>
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={submitReasonFlow}
                  disabled={actionLoading || !reasonText.trim()}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {showContactModal ? (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(1px)" }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered" role="document" style={{ maxWidth: 420 }}>
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: 14 }}>
              <div
                className="modal-header border-0 pb-1"
                style={{
                  background: "linear-gradient(90deg, #fff7e2 0%, #ffffff 100%)",
                  borderTopLeftRadius: 14,
                  borderTopRightRadius: 14,
                }}
              >
                <h5 className="modal-title fw-bold mb-0">Contact Support</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeContactModal}
                />
              </div>
              <div className="modal-body pt-2 pb-3">
                <p className="text-muted mb-1" style={{ fontSize: 13 }}>
                  Support Number
                </p>
                <div
                  className="d-flex flex-wrap align-items-center gap-2 px-3 py-2 rounded-3 border"
                  style={{ backgroundColor: "#fffdf6", borderColor: "#f0ddb0" }}
                >
                  <span
                    className="fw-bold flex-grow-1"
                    style={{ letterSpacing: 0.3, minWidth: "8.5rem" }}
                  >
                    {SUPPORT_CONTACT_DISPLAY}
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center justify-content-center p-0"
                    style={{ width: 38, height: 34 }}
                    onClick={handleCopySupportNumber}
                    title={contactCopyDone ? "Copied" : "Copy number"}
                    aria-label={contactCopyDone ? "Copied" : "Copy number"}
                  >
                    {contactCopyDone ? (
                      <FaCheck size={16} className="text-success" aria-hidden />
                    ) : (
                      <FaCopy size={15} aria-hidden />
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm d-inline-flex align-items-center justify-content-center p-0 text-white border-0"
                    style={{
                      width: 38,
                      height: 34,
                      backgroundColor: "#F8BA05",
                    }}
                    onClick={handleContactNow}
                    title={contactChannel === "whatsapp" ? "Open WhatsApp" : "Call now"}
                    aria-label={contactChannel === "whatsapp" ? "Open WhatsApp" : "Call now"}
                  >
                    {contactChannel === "whatsapp" ? (
                      <FaWhatsapp size={20} aria-hidden />
                    ) : (
                      <FaPhoneAlt size={16} aria-hidden />
                    )}
                  </button>
                </div>
                <div className="d-flex flex-column align-items-center mt-2">
                  <p className="text-muted text-center mb-2" style={{ fontSize: 12 }}>
                    Scan with your phone — opens{" "}
                    {contactChannel === "whatsapp" ? "WhatsApp" : "the dialer"} with this number
                  </p>
                  <div
                    className="p-2 rounded-3 border bg-white"
                    style={{ borderColor: "#f0ddb0", lineHeight: 0 }}
                  >
                    <QRCodeSVG
                      value={supportActionUrl}
                      size={200}
                      level="H"
                      marginSize={2}
                      title={
                        contactChannel === "whatsapp"
                          ? "Open WhatsApp with support number"
                          : "Call support number"
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <DateSelectModal
        onConfirm={handleDateConfirm}
        config={datePickerConfig}
        initialValueRef={datePickerInitialRef}
      />
      <WorkWithUs />
    </>
  );
};

export default BookingDetail;
